import {
  Box,
  Button,
  Grid,
  Heading,
  HStack,
  Icon,
  Stack,
  Text,
  useDisclosure,
  VStack
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import useIsSuperAdmin from '@hooks/useIsSuperAdmin';
import CopyableText from '@components/CopyableText';
import { getTxnMonitoringStatusColor } from '@utils';
import { formatDate } from '@utils/dateUtils';
import ReviewProviderInputModal from '@components/Modal/ReviewProviderModal';
import { providersToReviewConfig } from '@views/Thunes/utils';
import ModalLayout from '@components/Modal/ModalLayout';
import { MdInfoOutline, MdOutlineRateReview } from 'react-icons/md';
import JsonTextBox from '@components/JsonTextBlock';
import { REVIEWABLE_TRM_STATUSES } from './utils';


// Memoized sub-components extracted outside parent to prevent recreation on each render
const StatusIcon = React.memo(({ status, size = "12px" }) => (
  <Icon
    viewBox="0 0 200 200"
    color={getTxnMonitoringStatusColor(status)}
    w={size}
  >
    <path
      fill="currentColor"
      d="M 100, 100 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0"
    />
  </Icon>
));
StatusIcon.displayName = 'StatusIcon';

const InfoField = React.memo(({ label, value, isCopyable = false }) => (
  <Stack direction={{ base: "row", md: "column" }} justifyContent="space-between">
    <Text fontSize="sm" color="gray">
      {label}
    </Text>
    {isCopyable ? (
      <CopyableText fontSize="sm" color="white">
        {value}
      </CopyableText>
    ) : (
      <Text fontSize="sm" color="white">
        {value}
      </Text>
    )}
  </Stack>
));
InfoField.displayName = 'InfoField';

const StatusField = React.memo(({ label, status }) => (
  <HStack>
    <Text fontSize={{ base: "sm", sm: "md" }} color="gray">
      {label}:
    </Text>
    <StatusIcon status={status} />
    <Text fontSize={{ base: "sm", sm: "md" }} color="white">
      {status}
    </Text>
  </HStack>
));
StatusField.displayName = 'StatusField';

const DateField = React.memo(({ label, date }) => (
  <Stack direction={{ base: "column", sm: "row" }}>
    <Text fontSize={{ base: "sm", sm: "md" }} color="gray">
      {label}:
    </Text>
    <Text fontSize={{ base: "sm", sm: "md" }} color="white">
      {formatDate(date)}
    </Text>
  </Stack>
));
DateField.displayName = 'DateField';

const ProviderCard = React.memo(({ provider }) => (
  <VStack
    border="1px solid"
    borderColor="whiteAlpha.300"
    borderRadius="12px"
    p={4}
    width="100%"
    alignItems="stretch"
    bg="black"
    _hover={{ bg: "#333333" }}
  >
    <HStack>
      <StatusIcon status={provider.status} />
      <Text fontSize={{ base: "sm", sm: "md" }} color="white">
        {provider.status}
      </Text>
    </HStack>
    <DateField label="Created At" date={provider.createdAt} />
    <HStack>
      <Text fontSize={{ base: "sm", sm: "md" }} color="gray">
        Provider:
      </Text>
      <Text fontSize={{ base: "sm", sm: "md" }} color="white">
        {provider.provider}
      </Text>
    </HStack>
  </VStack>
));
ProviderCard.displayName = 'ProviderCard';

const FulfillmentInfo = React.memo(({ fulfillmentData }) => {
  // Memoize basicInfo to prevent recreation on every render
  const basicInfo = useMemo(() => [
    { label: "Resolving Provider", value: fulfillmentData?.additionalDetails?.riskDetails?.riskData?.resolvingProvider || "-" },
    { label: "Final TRM Status", value: fulfillmentData?.additionalDetails?.riskDetails?.riskData?.status || "-", isStatus: true },
    { label: "Last Updated At", value: formatDate(fulfillmentData?.additionalDetails?.riskDetails?.riskData?.updatedAt) || "-" },
    { label: "Order ID", value: fulfillmentData?.orderId || "-", isCopyable: true },
    { label: "User ID", value: fulfillmentData?.ownerId || "-", isCopyable: true },
    { label: "Provider Client Id", value: fulfillmentData?.additionalDetails?.riskDetails?.riskData?.providerClientId || "-" },
    { label: "Tags", value: fulfillmentData?.additionalDetails?.riskDetails?.riskData?.tags?.join(", ") || "-" },
  ], [fulfillmentData]);

  const remarksData = useMemo(() => {
    const remarks = fulfillmentData?.additionalDetails?.riskDetails?.riskData?.remarks;
    if (!remarks || remarks === "-") return null;
    try {
      return typeof remarks === 'string' ? JSON.parse(remarks) : remarks;
    } catch {
      return remarks;
    }
  }, [fulfillmentData?.additionalDetails?.riskDetails?.riskData?.remarks]);

  return (
    <VStack
      w={'full'}
      overflowY={{ base: 'initial', lg: 'scroll' }}
      overflowX="hidden"
      alignItems="stretch"
      gap={4}
    >
      {/* Primary details section */}
      <Grid
        bg="backGround"
        borderRadius="12px"
        gridTemplateColumns={{ base: "1fr" }}
        rowGap={2}
        p="1rem"
      >
        <Text fontSize={{ base: "sm", sm: "md" }} color="white" fontWeight={'semibold'} mb={2}>
          Fulfillment Details
        </Text>
        <HStack>
          <Text fontSize={{ base: "sm", sm: "md" }} color="gray">
            Fulfillment ID:
          </Text>
          <CopyableText
            title="Fulfillment ID"
            fontSize={{ base: "sm", sm: "md" }}
            color="white"
          >
            {fulfillmentData.fulfillmentId}
          </CopyableText>
        </HStack>

        <HStack>
          <Text fontSize={{ base: "sm", sm: "md" }} color="gray">
            Product:
          </Text>
          <Text fontSize={{ base: "sm", sm: "md" }} color="white">
            {fulfillmentData.product}
          </Text>
        </HStack>

        <StatusField label="Fulfillment Status" status={fulfillmentData.status} />
        <StatusField label="Fulfillment Sub Status" status={fulfillmentData.subStatus} />
        <DateField label="Created At" date={fulfillmentData.createdAt} />
        <DateField label="Updated At" date={fulfillmentData.updatedAt} />
      </Grid>

      {/* Final TRM details section */}
      <Grid
        bg="backGround"
        borderRadius="12px"
        gridTemplateColumns={{ base: "1fr" }}
        rowGap={2}
        p="1rem"
      >
        <Text fontSize={{ base: "sm", sm: "md" }} color="white" fontWeight={'semibold'} mb={2}>
          Final TRM Details
        </Text>
        {basicInfo.map((info, index) => (
          info.isStatus ? (
            <StatusField
              key={index}
              label={info.label}
              status={info.value}
            />
          ) : (
            <InfoField
              key={index}
              label={info.label}
              value={info.value}
              isCopyable={info.isCopyable}
            />
          )
        ))}
        {/* Remarks section with JSON viewer */}
        {remarksData && (
          <VStack alignItems="stretch" gap={2} w="full" minW={0}>
            <Text fontSize="sm" color="gray">
              Remarks
            </Text>
            <Box w="full" overflowX="auto">
              <JsonTextBox data={remarksData}/>
            </Box>
          </VStack>
        )}
      </Grid>

      {/* Entity providers section */}
      <Grid
        bg="backGround"
        borderRadius="12px"
        gridTemplateColumns={{ base: "1fr" }}
        rowGap={2}
        p="1rem"
      >
        <Text fontSize={{ base: "sm", sm: "md" }} color="white" fontWeight={'semibold'} mb={2}>
          Entity Providers
        </Text>
        {fulfillmentData?.additionalDetails?.riskDetails?.providerRiskData?.length ? (
          <VStack alignItems="stretch" gap={2} w={'full'}>
            {fulfillmentData?.additionalDetails?.riskDetails?.providerRiskData?.map((provider, index) => (
              <ProviderCard
                key={provider?.id || provider?.provider || `provider-${index}`}
                provider={provider}
              />
            ))}
          </VStack>
        ) : (
          <Text color="white" fontSize="md">
            No Providers Data found
          </Text>
        )}
      </Grid>
    </VStack>
  );
});
FulfillmentInfo.displayName = 'FulfillmentInfo';

const GomsFulfillmentDetails = ({
  fulfillmentData,
  triggerRefreshFulfillmentDashboard
}) => {
  const dispatch = useDispatch();
  const isSuperAdmin = useIsSuperAdmin();
  const [providersToReview, setProvidersToReview] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const {
    isOpen: isProviderToReviewModalOpen,
    onOpen: onProviderToReviewModalOpen,
    onClose: onProviderToReviewModalClose
  } = useDisclosure();

  const providerRiskData = useMemo(
    () => fulfillmentData?.additionalDetails?.riskDetails?.providerRiskData?.map((item) => item?.provider),
    [fulfillmentData?.additionalDetails?.riskDetails?.providerRiskData]
  );

  const finalTrmStatus = fulfillmentData?.additionalDetails?.riskDetails?.riskData?.status;
  const shouldShowReviewSection =
    isSuperAdmin &&
    providersToReview?.length > 0 &&
    REVIEWABLE_TRM_STATUSES.includes(finalTrmStatus);

  // Update providers to review when fulfillment data changes
  useEffect(() => {
    if (providerRiskData) {
      setProvidersToReview(providerRiskData);
    }
    return () => {
      setProvidersToReview(null);
    }
  }, [providerRiskData]);

  const handleProvidersToReviewSubmit = useCallback((formData) => {
    setIsModalLoading(true);
    dispatch(
      providersToReviewConfig.reviewFulfillmentProvider({
        fulfillmentId: fulfillmentData?.fulfillmentId,
        payload: formData,
        onSuccess: () => {
          triggerRefreshFulfillmentDashboard();
        },
        onComplete: () => {
          setIsModalLoading(false);
          onProviderToReviewModalClose();
        }
      })
    );
  }, [dispatch, fulfillmentData?.fulfillmentId, triggerRefreshFulfillmentDashboard, onProviderToReviewModalClose]);


  // Early return if no data
  if (!fulfillmentData?.fulfillmentId) {
    return (
      <Box position="relative" width="100%">
        <Text color="white" textAlign="center" p={4}>
          No fulfillment data available
        </Text>
      </Box>
    );
  }

  return (
    <Box position="relative" width="100%">
      <ModalLayout
        isOpen={isProviderToReviewModalOpen}
        onClose={onProviderToReviewModalClose}
      >
        <ReviewProviderInputModal
          title={'Review Your Transaction'}
          onClose={onProviderToReviewModalClose}
          fieldsGroup={
            providersToReviewConfig.providersToReviewFieldsConfig(
              providersToReview
            ) || []
          }
          onSubmit={handleProvidersToReviewSubmit}
          isLoading={isModalLoading}
        />
      </ModalLayout>
      <VStack
        overflowY={{ base: 'initial', lg: 'scroll' }}
        overflowX="hidden"
        alignItems="stretch"
        gap={4}
      >
        <VStack w="100%" gap={4}>
          <Heading
            px={4}
            color="white"
            fontSize="1.3rem"
            alignSelf="start"
          >
            Fulfillment Provider Details
          </Heading>

          <FulfillmentInfo fulfillmentData={fulfillmentData} />

          {shouldShowReviewSection && (
              <HStack
                w={'100%'}
                gap={2}
                alignSelf={'start'}
                justifyContent={'space-between'}
                border={'1px solid'}
                borderColor={'whiteAlpha.500'}
                bg="backGround"
                borderRadius="12px"
                p={4}
              >
                <MdInfoOutline size={'1.5rem'} />
                <Text fontSize={'sm'}>Review Your Transaction</Text>
                <Button
                  colorScheme={'brand'}
                  size="sm"
                  bg="backGround"
                  border={'2px solid'}
                  borderColor={'whiteAlpha.500'}
                  borderRadius={'full'}
                  p={4}
                  leftIcon={<MdOutlineRateReview />}
                  onClick={onProviderToReviewModalOpen}
                >
                  Review
                </Button>
              </HStack>
            )}
        </VStack>
      </VStack>
    </Box>
  );
};

export default GomsFulfillmentDetails;